import { theme } from 'antd'

/**
 * Tema de Ant Design como design tokens.
 *
 * Sustituye a components/kit-vendors/antd/themes/{default,dark}.less, que
 * funcionaban importando antd/dist/antd.less y sobreescribiendo variables Less.
 * antd 5 eliminó Less por completo, así que las ~60 variables de aquellos dos
 * ficheros se traducen a tokens de ConfigProvider.
 *
 * Los valores hex son los mismos de antes. Los que en Less salían de darken()
 * o lighten() vienen ya resueltos y se indica su origen en un comentario, para
 * que se pueda auditar el mapeo sin tener que recalcular nada.
 */

const kit = {
  white: '#ffffff',
  black: '#141322',
  gray1: '#f0f2f4',
  gray2: '#e4e9f0',
  gray3: '#d9dee9',
  gray6: '#595c97',
  grayDark1: '#aeaee0',
  grayDark2: '#7575a3',
  grayDark4: '#232135',
  blue: '#4b7cf3',
  blueLight: '#42baf9',
  red: '#f5222e',
  green: '#41b883',
  orange: '#faad15',
}

// Colores que en Less se calculaban. Se dejan explícitos para no depender de
// una función de color en tiempo de ejecución.
const derived = {
  // lighten(@kit-gray-6, 10%)
  placeholderLight: '#7578ae',
  // darken(@kit-gray-dark-4, 3%)
  disabledBgDark: '#1d1b2c',
  // darken(@kit-gray-dark-4, 4%)
  tableRowHoverDark: '#1b1928',
  // lighten(@kit-gray-dark-4, 7%)
  itemHoverDark: '#322f4b',
}

const fontFamily = '"Roboto Condensed", sans-serif'

/** Las tres capas de Layout eran transparentes en ambos temas. */
const transparentLayout = {
  bodyBg: 'transparent',
  headerBg: 'transparent',
  footerBg: 'transparent',
}

const light = {
  algorithm: theme.defaultAlgorithm,
  token: {
    fontFamily,

    colorPrimary: kit.blue,
    colorInfo: kit.blue,
    colorSuccess: kit.green,
    colorError: kit.red,
    colorWarning: kit.orange,

    colorText: kit.gray6,
    colorTextHeading: kit.gray6,
    colorTextPlaceholder: derived.placeholderLight,
    colorTextDisabled: kit.gray6,

    colorBgLayout: kit.white,
    colorBgContainer: kit.white,
    colorBgContainerDisabled: kit.gray2,

    colorBorder: kit.gray2,
    colorSplit: kit.gray2,

    colorLink: kit.blue,
    colorLinkHover: kit.blueLight,
    colorLinkActive: kit.blueLight,

    // @item-hover-bg / @item-active-bg
    controlItemBgHover: kit.gray2,
    controlItemBgActive: kit.gray2,

    // @alert-*-bg-color / @alert-*-border-color
    colorSuccessBg: '#f6ffed',
    colorSuccessBorder: '#b7eb8f',
    colorInfoBg: '#e6f7ff',
    colorInfoBorder: '#91d5ff',
    colorWarningBg: '#fffbe6',
    colorWarningBorder: '#ffe58f',
    colorErrorBg: '#fff1f0',
    colorErrorBorder: '#ffa39e',
  },
  components: {
    Layout: transparentLayout,
    Button: {
      primaryColor: kit.white,
      defaultColor: kit.gray6,
      defaultBg: kit.white,
      dangerColor: kit.white,
    },
    Input: {
      addonBg: kit.gray1,
    },
    Table: {
      headerBg: kit.gray1,
      headerColor: kit.gray6,
      footerBg: kit.gray1,
      footerColor: kit.gray6,
      rowHoverBg: kit.gray2,
      rowSelectedBg: kit.gray1,
      rowSelectedHoverBg: kit.gray2,
    },
    Tabs: {
      itemHoverColor: kit.blueLight,
      itemSelectedColor: kit.blue,
      inkBarColor: kit.blue,
    },
    Form: {
      labelColor: kit.gray6,
      labelRequiredMarkColor: kit.red,
    },
    Menu: {
      itemHoverBg: kit.gray2,
      itemSelectedBg: kit.gray2,
    },
  },
}

const dark = {
  algorithm: theme.darkAlgorithm,
  token: {
    fontFamily,

    colorPrimary: kit.blue,
    colorInfo: kit.blueLight,
    colorSuccess: kit.green,
    colorError: kit.red,
    // El dark.less original ponía @warning-color: @kit-red, mientras que el
    // tema claro usa naranja y el propio fichero pinta de naranja el icono del
    // Alert de aviso. Se mantiene el rojo por fidelidad, pero se deja anotado:
    // en antd 5 el icono del Alert deriva de este token, así que en modo oscuro
    // ese icono pasa de naranja a rojo. Es la única desviación visual conocida.
    colorWarning: kit.red,

    colorText: kit.grayDark1,
    colorTextHeading: kit.white,
    colorTextPlaceholder: kit.grayDark2,
    colorTextDisabled: kit.grayDark2,

    colorBgLayout: kit.black,
    colorBgContainer: kit.grayDark4,
    colorBgContainerDisabled: derived.disabledBgDark,

    colorBorder: kit.grayDark4,
    colorSplit: kit.grayDark4,

    colorLink: kit.white,
    colorLinkHover: kit.blue,
    colorLinkActive: kit.blueLight,

    controlItemBgHover: derived.itemHoverDark,
    controlItemBgActive: derived.itemHoverDark,

    // En oscuro los Alert eran todos del color del contenedor, sin borde propio.
    colorSuccessBg: kit.grayDark4,
    colorSuccessBorder: kit.grayDark4,
    colorInfoBg: kit.grayDark4,
    colorInfoBorder: kit.grayDark4,
    colorWarningBg: kit.grayDark4,
    colorWarningBorder: kit.grayDark4,
    colorErrorBg: kit.grayDark4,
    colorErrorBorder: kit.grayDark4,
  },
  components: {
    Layout: transparentLayout,
    Button: {
      primaryColor: kit.white,
      defaultColor: kit.white,
      defaultBg: kit.grayDark4,
      dangerColor: kit.white,
    },
    Input: {
      addonBg: derived.itemHoverDark,
    },
    Table: {
      headerBg: kit.grayDark4,
      headerColor: kit.grayDark1,
      footerBg: kit.grayDark4,
      footerColor: kit.grayDark1,
      rowHoverBg: derived.tableRowHoverDark,
      rowSelectedBg: kit.grayDark4,
      rowSelectedHoverBg: derived.tableRowHoverDark,
    },
    Tabs: {
      itemHoverColor: kit.blue,
      itemSelectedColor: kit.blue,
      inkBarColor: kit.blue,
    },
    Form: {
      labelColor: kit.grayDark1,
      labelRequiredMarkColor: kit.grayDark1,
    },
    Menu: {
      itemHoverBg: derived.itemHoverDark,
      itemSelectedBg: derived.itemHoverDark,
    },
  },
}

/**
 * Modo "Alto contraste" (botón del TopBar): texto y bordes de campos en negro.
 * El color es el mismo --kit-text-color que activa
 * html[data-alto-contraste='true'] en components/kit-core/css/core.scss.
 */
const altoContrasteColor = '#000000'

// colorBorder se sobreescribe solo en los campos de formulario para no
// oscurecer tarjetas, tablas ni botones, que siguen con el colorBorder global.
const fieldBorder = { colorBorder: altoContrasteColor }

const altoContraste = {
  token: {
    colorText: altoContrasteColor,
    colorTextHeading: altoContrasteColor,
  },
  components: {
    Button: { defaultColor: altoContrasteColor },
    Table: { headerColor: altoContrasteColor, footerColor: altoContrasteColor },
    Form: { labelColor: altoContrasteColor },
    Input: fieldBorder,
    InputNumber: fieldBorder,
    Select: fieldBorder,
    DatePicker: fieldBorder,
    Cascader: fieldBorder,
    TreeSelect: fieldBorder,
    Mentions: fieldBorder,
  },
}

const withOverlay = (base, overlay) => ({
  ...base,
  token: { ...base.token, ...overlay.token },
  components: Object.fromEntries(
    [...new Set([...Object.keys(base.components), ...Object.keys(overlay.components)])].map(
      name => [name, { ...base.components[name], ...overlay.components[name] }],
    ),
  ),
})

// Se calcula una sola vez para que ConfigProvider reciba siempre el mismo objeto
// y no regenere los estilos en cada render.
const lightAltoContraste = withOverlay(light, altoContraste)

/**
 * @param {'default'|'dark'} name Valor de settings.theme en Redux.
 * @param {boolean} [altoContrasteActivo] Valor de settings.altoContraste. Solo
 *   afecta al tema claro: texto negro sobre el fondo oscuro no se leería.
 */
export const getAntdTheme = (name, altoContrasteActivo = false) => {
  if (name === 'dark') return dark
  return altoContrasteActivo ? lightAltoContraste : light
}

export default getAntdTheme
